import { Link } from "react-router-dom";
import { toAbsoluteUrl } from "../../_metronic/helpers";

const PrivacyPolicyPage = () => (
  <div className="d-flex flex-column flex-root min-vh-100 bg-light">
    <div className="container py-10 py-lg-15">
      <div className="mx-auto" style={{ maxWidth: "900px" }}>
        <div className="mb-8 text-center">
          <Link to="/">
            <img
              alt="Orderlink Logo"
              src={toAbsoluteUrl("/media/orderlink_logos/Blue.svg")}
              className="h-50px"
            />
          </Link>
        </div>
        <div className="card card-flush shadow-sm">
          <div className="card-body p-8 p-lg-12 text-gray-800">
            <h1 className="fs-2qx fw-bold mb-2">Privacy Policy</h1>
            <p className="text-muted fs-6 mb-10">Last updated: 15 October 2026</p>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">1. Introduction</h2>
              <p className="mb-3">
                Orderlink NZ Limited (&quot;Orderlink&quot;, &quot;we&quot;, &quot;us&quot;, or
                &quot;our&quot;) is committed to protecting the privacy and security of the
                information entrusted to us.
              </p>
              <p className="mb-3">
                This Privacy Policy explains how we collect, use, store, disclose, and protect
                personal information when you visit our website, use our software platform, connect
                third-party services, or otherwise interact with us.
              </p>
              <p className="mb-3">
                Orderlink provides software and services that help businesses manage inventory,
                purchasing, demand planning, shipments, and other supply chain operations.
              </p>
              <p className="mb-3">
                This Privacy Policy applies to our websites, applications, software, and related
                services (collectively referred to as the &quot;Services&quot;).
              </p>
              <p className="mb-0">
                By accessing or using our Services, you acknowledge the information practices
                described in this Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">2. Information We Collect</h2>
              <p className="mb-4">
                We collect information necessary to provide, maintain, and improve our Services.
              </p>
              <h3 className="fs-5 fw-semibold mb-3">Account and Business Information</h3>
              <p className="mb-2">
                When you register for an account, contact us, or use our Services, we may collect:
              </p>
              <ul className="mb-4">
                <li>Your name, email address, and contact information.</li>
                <li>Business name, address, and contact details.</li>
                <li>Account credentials and authentication information.</li>
                <li>Billing and subscription information.</li>
                <li>Communications with our team, including support requests and enquiries.</li>
              </ul>
              <h3 className="fs-5 fw-semibold mb-3">Business Operations Information</h3>
              <p className="mb-2">
                When using our platform, you may provide or authorise us to access information
                relating to your business operations, including:
              </p>
              <ul className="mb-4">
                <li>Products, SKUs, and inventory records.</li>
                <li>Suppliers, customers, and business contacts.</li>
                <li>Purchase orders and sales orders.</li>
                <li>Warehouse and fulfilment information.</li>
                <li>Shipment and logistics information.</li>
                <li>Manufacturing and production records.</li>
                <li>Sales history and demand forecasts.</li>
                <li>Financial and accounting information.</li>
              </ul>
              <p className="mb-4">
                Some of this information may contain personal information, such as customer names,
                email addresses, telephone numbers, and delivery addresses.
              </p>
              <h3 className="fs-5 fw-semibold mb-3">Information From Third-Party Integrations</h3>
              <p className="mb-3">
                Our Services allow you to connect external applications and platforms, including
                ecommerce, accounting, and logistics systems.
              </p>
              <p className="mb-3">
                When you authorise these connections, we may collect and process information made
                available through those services, depending on the permissions granted and the
                functionality enabled.
              </p>
              <p className="mb-4">
                We only seek access to information reasonably necessary to provide the relevant
                functionality.
              </p>
              <h3 className="fs-5 fw-semibold mb-3">Website and Usage Information</h3>
              <p className="mb-2">
                When you visit our website or use our platform, we may automatically collect certain
                technical information, including:
              </p>
              <ul className="mb-4">
                <li>IP addresses and approximate geographic location.</li>
                <li>Browser type, operating system, and device information.</li>
                <li>Pages visited and interactions with our website.</li>
                <li>Application activity and usage information.</li>
                <li>Cookies and similar tracking technologies.</li>
                <li>Technical logs, error reports, and diagnostic information.</li>
              </ul>
              <p className="mb-0">
                We use this information to maintain our Services, understand how they are used, and
                improve their performance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">3. How We Use Information</h2>
              <p className="mb-2">
                We use the information we collect to operate our business and provide our Services.
              </p>
              <p className="mb-2">This includes:</p>
              <ul className="mb-4">
                <li>Creating and managing user accounts.</li>
                <li>
                  Providing inventory, purchasing, shipment, and supply chain management
                  functionality.
                </li>
                <li>Synchronising information between connected applications.</li>
                <li>Processing and managing business transactions and operational records.</li>
                <li>Generating forecasts, reports, and planning recommendations.</li>
                <li>Providing technical support and responding to enquiries.</li>
                <li>Managing subscriptions, billing, and payments.</li>
                <li>Improving the functionality, reliability, and usability of our Services.</li>
                <li>Monitoring security and preventing unauthorised access or misuse.</li>
                <li>Communicating important updates about our Services.</li>
                <li>Complying with applicable legal and regulatory obligations.</li>
              </ul>
              <p className="mb-3">
                We may also use your business contact information to communicate relevant information
                about our products and services, subject to applicable marketing laws and your
                communication preferences.
              </p>
              <p className="mb-0">We do not sell personal information to third parties.</p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">4. How We Share Information</h2>
              <p className="mb-4">
                We may share information with trusted third parties where necessary to operate our
                business and deliver our Services.
              </p>
              <h3 className="fs-5 fw-semibold mb-3">Service Providers</h3>
              <p className="mb-3">
                We may engage third-party providers to support our operations, including cloud
                hosting, data storage, payment processing, email communications, analytics, and
                technical infrastructure.
              </p>
              <p className="mb-4">
                These providers may process information on our behalf to deliver their services.
              </p>
              <h3 className="fs-5 fw-semibold mb-3">Third-Party Integrations</h3>
              <p className="mb-3">
                Where you authorise Orderlink to connect with external applications, we may exchange
                relevant information with those applications to provide the requested functionality.
              </p>
              <p className="mb-4">
                This may include integrations with ecommerce platforms such as Shopify, accounting
                systems, logistics providers, and other business software.
              </p>
              <h3 className="fs-5 fw-semibold mb-3">Legal and Regulatory Requirements</h3>
              <p className="mb-3">
                We may disclose information where required by law, regulation, court order, or other
                lawful request.
              </p>
              <p className="mb-4">
                We may also disclose information where reasonably necessary to protect our rights,
                investigate suspected fraud, or maintain the security of our Services.
              </p>
              <h3 className="fs-5 fw-semibold mb-3">Business Transfers</h3>
              <p className="mb-4">
                If Orderlink undergoes a merger, acquisition, restructuring, or sale of assets,
                information may be transferred as part of that transaction, subject to applicable
                privacy laws.
              </p>
              <p className="mb-0">
                We do not share personal information with third parties for their independent
                marketing purposes without an appropriate legal basis.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">5. Data Storage and Security</h2>
              <p className="mb-3">
                We take reasonable technical and organisational measures to protect information
                against unauthorised access, loss, misuse, alteration, and disclosure.
              </p>
              <p className="mb-2">These measures may include:</p>
              <ul className="mb-4">
                <li>Secure cloud hosting and infrastructure.</li>
                <li>Access controls and authentication systems.</li>
                <li>Encryption of information where appropriate.</li>
                <li>Restricted access to sensitive information.</li>
                <li>Security monitoring and system maintenance.</li>
                <li>Regular software updates and security improvements.</li>
              </ul>
              <p className="mb-3">
                Access to customer information is limited to authorised personnel and service
                providers who require it to perform their responsibilities.
              </p>
              <p className="mb-3">
                While we take reasonable precautions to protect information, no method of electronic
                transmission or storage can guarantee absolute security.
              </p>
              <p className="mb-0">
                Users are responsible for maintaining the confidentiality of their account
                credentials and ensuring that their accounts are accessed only by authorised
                individuals.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">6. Data Retention</h2>
              <p className="mb-3">
                We retain information for as long as reasonably necessary to provide our Services,
                fulfil legitimate business purposes, and comply with applicable legal obligations.
              </p>
              <p className="mb-3">
                Retention periods may vary depending on the nature of the information, how it is
                used, and any applicable legal requirements.
              </p>
              <p className="mb-3">
                When an account is closed or a third-party integration is disconnected, we will
                handle the associated information in accordance with our data retention procedures
                and applicable legal requirements.
              </p>
              <p className="mb-3">
                Certain information may be retained where necessary for legal compliance, financial
                recordkeeping, security, dispute resolution, or other lawful purposes.
              </p>
              <p className="mb-0">
                When information is no longer required, we take reasonable steps to securely delete
                or de-identify it.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">7. Your Privacy Rights</h2>
              <p className="mb-3">
                Depending on your location and applicable privacy laws, you may have certain rights
                regarding your personal information.
              </p>
              <p className="mb-2">These may include the right to:</p>
              <ul className="mb-4">
                <li>Request access to personal information we hold about you.</li>
                <li>Request correction of inaccurate or incomplete information.</li>
                <li>Request deletion of personal information where applicable.</li>
                <li>Withdraw consent where processing is based on consent.</li>
                <li>Object to or restrict certain uses of personal information where applicable.</li>
                <li>Lodge a complaint with a relevant privacy authority.</li>
              </ul>
              <p className="mb-3">
                To exercise these rights, please contact us using the details provided at the end
                of this Privacy Policy.
              </p>
              <p className="mb-3">
                Where we process personal information on behalf of a business customer, we may
                direct relevant requests to that business or assist them in responding.
              </p>
              <p className="mb-0">
                We will respond to requests in accordance with applicable privacy laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">8. International Data Transfers</h2>
              <p className="mb-3">
                Orderlink is based in New Zealand and provides Services to businesses operating
                internationally.
              </p>
              <p className="mb-3">
                As part of delivering our Services, information may be stored, accessed, or processed
                in countries outside New Zealand.
              </p>
              <p className="mb-3">
                This may occur where our hosting providers, technology partners, or other service
                providers operate internationally.
              </p>
              <p className="mb-0">
                Where required by applicable privacy laws, we take reasonable steps to ensure that
                personal information transferred internationally is subject to appropriate
                safeguards. These safeguards may include contractual protections and other measures
                designed to maintain an appropriate level of privacy and security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">9. Cookies and Analytics</h2>
              <p className="mb-3">
                Our website and Services may use cookies and similar technologies to support
                functionality, analyse usage, and improve the user experience.
              </p>
              <p className="mb-3">
                Cookies are small files stored on your device that help websites recognise browsers
                and remember certain information.
              </p>
              <p className="mb-2">We may use cookies and analytics technologies to:</p>
              <ul className="mb-4">
                <li>Maintain website and application functionality.</li>
                <li>Understand how visitors interact with our website.</li>
                <li>Analyse website traffic and usage patterns.</li>
                <li>Identify technical issues and improve performance.</li>
                <li>Remember user preferences where applicable.</li>
              </ul>
              <p className="mb-3">
                We may use third-party analytics services to better understand website interactions
                and improve our Services.
              </p>
              <p className="mb-3">
                You can manage or disable certain cookies through your browser settings.
              </p>
              <p className="mb-3">
                Disabling cookies may affect the functionality of some parts of our website or
                Services.
              </p>
              <p className="mb-0">
                Where required by applicable law, we will obtain consent before using non-essential
                cookies or tracking technologies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">10. Third-Party Websites and Services</h2>
              <p className="mb-3">
                Our website and platform may contain links to, or integrate with, third-party
                websites, applications, and services.
              </p>
              <p className="mb-3">
                These third parties operate independently and may have their own privacy policies
                and data handling practices.
              </p>
              <p className="mb-3">
                We encourage users to review the privacy policies of any third-party services they
                choose to access or connect.
              </p>
              <p className="mb-0">
                Orderlink is not responsible for the independent privacy practices of third-party
                websites or services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">11. Children&apos;s Privacy</h2>
              <p className="mb-3">
                Our Services are intended for business use and are not directed towards children.
              </p>
              <p className="mb-0">
                We do not knowingly collect personal information directly from children for the
                purpose of creating or operating Orderlink accounts. If we become aware that
                personal information has been collected from a child in circumstances that require
                its removal, we will take appropriate steps in accordance with applicable law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="fs-3 fw-bold mb-4">12. Changes to This Privacy Policy</h2>
              <p className="mb-3">
                We may update this Privacy Policy periodically to reflect changes in our Services,
                business practices, technology, or legal obligations.
              </p>
              <p className="mb-3">
                Any updates will be published on this page, together with a revised &quot;Last
                updated&quot; date.
              </p>
              <p className="mb-3">
                Where required by law, we will provide additional notice of material changes.
              </p>
              <p className="mb-0">
                We encourage users to review this Privacy Policy periodically to stay informed about
                how we handle information.
              </p>
            </section>

            <section>
              <h2 className="fs-3 fw-bold mb-4">13. Contact Us</h2>
              <p className="mb-3">
                If you have any questions about this Privacy Policy, wish to exercise your privacy
                rights, or have concerns about how your information is handled, please contact us.
              </p>
              <p className="mb-1 fw-semibold">Orderlink NZ Limited</p>
              <p className="mb-1">Auckland, New Zealand</p>
              <p className="mb-3">
                Email:{" "}
                <a href="mailto:privacy@orderlink.io" className="text-primary">
                  privacy@orderlink.io
                </a>
              </p>
              <p className="mb-0">
                We will endeavour to respond to privacy-related enquiries within a reasonable
                timeframe and in accordance with applicable legal requirements.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export { PrivacyPolicyPage };
